import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilterParameter, EnumHelper, QueryParamsService } from 'common';
import { SimpleDialogService, CommonActionKey } from 'common';
import { readWrite } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { LogFilterService } from '../log-filter.service';
import { LogListFilterDialogComponent } from '../log-list-filter-dialog/log-list-filter-dialog.component';
import {
  LogGroupByOption,
  LogFilters,
  LogQueryResult,
  LogQueryParams,
  LogGroupQueryResult,
  LogGroupByLabel,
  LogFilterOptions
} from '../log.model';
import { LogService } from '../log.service';

@UntilDestroy()
@Component({
    selector: 'ifb-log-shell',
    templateUrl: './log-shell.component.html',
    styleUrls: ['./log-shell.component.scss'],
    standalone: false
})
export class LogShellComponent implements OnInit {
  grouped = false;
  expanded = false;
  showExpandAllButton = false;
  canReadWriteLog = false;
  filters: LogFilters;
  activeFilters: FilterParameter[] = [];
  queryParams: any;
  activeGroupBy: any;
  data: LogGroupedComponentData;
  logGroupByOptions = EnumHelper.getMappedNamesAndValues(LogGroupByOption, LogGroupByLabel);
  pageChangedSubject: Subject<void> = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly logFilterService: LogFilterService,
    private readonly logService: LogService,
    private readonly userPermissionService: UserPermissionService,
    private readonly dialog: MatDialog,
    private readonly queryParamsService: QueryParamsService,
    private readonly simpleDialogService: SimpleDialogService
  ) {}

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe((it: LogGroupedComponentData) => this.data = it);

    this.route.queryParams
      .pipe(untilDestroyed(this))
      .subscribe(it => {
        this.queryParams = this.queryParamsService.init(it);
        this.grouped = this.queryParams.groupBy !== undefined;
        this.activeFilters = this.logFilterService.addFilterParameters(this.queryParams);
        this.filters = new LogFilters(this.queryParams);
      });

    this.userPermissionService.granted([readWrite('admin-logs')])
      .subscribe(res => this.canReadWriteLog = res);

    this.setPaginator(this.grouped ? this.paginatorGrouped : this.paginatorList);
  }

  private setPaginator(paginator: MatPaginator): void {
    paginator.pageSize = this.queryParams.limit;
    paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
  }

  group(groupBy: LogGroupByOption): void {
    if (!groupBy)
      this.setGroup(undefined, false);
    else this.setGroup(groupBy, true);
  }

  setGroup(groupBy: LogGroupByOption, isGrouped: boolean): void {
    this.grouped = isGrouped;
    this.activeGroupBy = groupBy;
    this.queryParams = Object.assign({}, this.queryParams, { groupBy: groupBy });
    this.logFilterService.filter(this.queryParams, isGrouped ? this.paginatorGrouped : this.paginatorList, this.sort);
  }

  archiveAll(): void {
    this.simpleDialogService.openSimpleConfirmDialog('Are you sure you want to archive all logs?', 'Confirm')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.logService.archiveAll(this.queryParams).subscribe(() => {
            this.fetchData();
          });
        }
      });
  }

  fetchData(): void {
    if (this.grouped)
      this.logService.grouped(new LogQueryParams(this.queryParams))
        .subscribe(res => this.data.logGroupQueryResult = res);
    else this.logService.query(new LogQueryParams(this.queryParams))
      .subscribe(res => this.data.logQueryResult = res);
  }

  activeFilterRemoved(activefilters: FilterParameter[]): void {
    if (!activefilters.length) return;
    activefilters.forEach(item => {
      this.filters.remove(item.id);
      this.logFilterService.filter(this.filters);
    });

    if (this.filters.groupBy === undefined) {
      this.grouped = false;
      this.activeGroupBy = undefined;
    }
  }

  filterDialog(): void {
    LogListFilterDialogComponent.show(this.dialog, this.queryParams, this.data.logFilterOptions)
      .subscribe(result => {
        if (result) {
          result.entity = result.entity ? result.entity.id : undefined;
          result.loan = result.loan ? result.loan.id : undefined;
          result.customer = result.customer ? result.customer.id : undefined;
          this.filters = result;
          if (this.grouped) {
            this.filters = Object.assign({}, this.filters, { groupBy: this.activeGroupBy });
          }
          this.paginatorList.firstPage();
          this.paginatorGrouped.firstPage();
          this.logFilterService.filter(this.filters, this.grouped ? this.paginatorGrouped : this.paginatorList, this.sort);
        }
      });
  }

  onNestedFiltered(params: LogQueryParams): void {
    this.filters = null;
    this.filters = Object.assign({}, this.filters, params);
    this.paginatorList.firstPage();
    this.logFilterService.filter(this.filters, this.paginatorList, this.sort);
  }

  onShowMessagesSelected(params: LogQueryParams): void {
    this.grouped = false;
    this.activeGroupBy = undefined;
    this.onNestedFiltered(params);
  }

  @ViewChild(MatSort)
  sort: MatSort;

  @ViewChild('paginatorList', { static: true })
  paginatorList: MatPaginator;

  @ViewChild('paginatorGrouped', { static: true })
  paginatorGrouped: MatPaginator;

  setPage(): void {
    this.pageChangedSubject.next();
    if (this.grouped) {
      this.filters.groupBy = this.activeGroupBy;
    }
    this.logFilterService.filter(this.filters, this.grouped ? this.paginatorGrouped : this.paginatorList, this.sort);
  }
}

export interface LogGroupedComponentData {
  logGroupQueryResult?: LogGroupQueryResult;
  logQueryResult?: LogQueryResult;
  logFilterOptions?: LogFilterOptions;
}

